// @flow
import SHA256 from 'crypto-js/sha256';
import { TRACKING_EVENTS } from './events';

const PIXEL_ID = process.env.REACT_APP_OPENAI_ADS_PIXEL_ID || '';
const SDK_SRC = 'https://bzrcdn.openai.com/sdk/oaiq.min.js';
const SCRIPT_ID = 'openai-ads-pixel-sdk';

let initialized = false;
let userInitialized = false;

type User = {
  id?: string,
  [key: string]: any,
};

type EventProperties = {
  value?: number,
  price?: number,
  quantity?: number,
  currency?: string,
  path?: string,
  title?: string,
  transactionId?: string,
  productId?: string,
  productSlug?: string,
  productTitle?: string,
  productPriceId?: string,
  variant?: string,
  partNumber?: string,
  items?: any[],
  [key: string]: any,
};

const isBrowser = (): boolean =>
  typeof window !== 'undefined' && typeof document !== 'undefined';

const isProduction = (): boolean => process.env.NODE_ENV === 'production';

const getQueue = (): ?Function => {
  if (!isBrowser()) return null;
  const w: any = window;
  w.oaiq =
    w.oaiq ||
    function oaiq(...args) {
      (w.oaiq.q = w.oaiq.q || []).push(args);
    };
  return w.oaiq;
};

const loadSdk = (): void => {
  if (!isBrowser() || document.getElementById(SCRIPT_ID)) return;
  const script = document.createElement('script');
  script.id = SCRIPT_ID;
  script.async = true;
  script.src = SDK_SRC;
  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
    return;
  }
  document.head.appendChild(script);
};

const hashValue = (value: string): string => SHA256(value.trim().toLowerCase()).toString();

const buildUserData = (user?: ?User): ?Object => {
  if (!user || !user.id) return null;
  return {
    external_id_sha256: hashValue(String(user.id)),
  };
};

export const initialize = (user?: ?User): void => {
  if (!isProduction() || !PIXEL_ID || initialized) return;
  try {
    const oaiq = getQueue();
    if (!oaiq) return;
    loadSdk();
    const userData = buildUserData(user);
    oaiq('init', userData ? { pixelId: PIXEL_ID, user: userData } : { pixelId: PIXEL_ID });
    initialized = true;
    userInitialized = !!userData;
  } catch (e) {
    // Pixel failures must not affect the core product flow.
  }
};

export const identify = (user: User): void => {
  if (!isProduction() || !PIXEL_ID || userInitialized) return;
  try {
    if (!initialized) {
      initialize(user);
      return;
    }
    const userData = buildUserData(user);
    const oaiq = getQueue();
    if (!userData || !oaiq) return;
    oaiq('init', { user: userData });
    userInitialized = true;
  } catch (e) {
    // ignore
  }
};

const toFiniteNumber = (value: any): ?number => {
  const parsed = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const getCurrentPageContent = (properties: EventProperties = {}): Object => {
  const path =
    properties.path ||
    (isBrowser() && window.location ? window.location.pathname : '') ||
    '/';
  const title =
    properties.title ||
    (isBrowser() && document.title ? document.title : '') ||
    path;

  return {
    id: String(path),
    name: String(title),
    content_type: 'page',
  };
};

const buildContentItem = (item: any, fallbackContentType: string = 'product'): ?Object => {
  if (!item || typeof item !== 'object') return null;

  const content: any = {};
  const id =
    item.productPriceId ||
    item.productId ||
    item.id ||
    item.variant ||
    item.partNumber ||
    item.productSlug ||
    item.slug;
  const name = item.productTitle || item.name || item.title;
  const quantity = toFiniteNumber(item.quantity);
  const amount = toFiniteNumber(item.price);
  const currency = item.currency || 'KRW';

  if (id) content.id = String(id);
  if (name) content.name = String(name);
  content.content_type = item.contentType || item.content_type || fallbackContentType;
  if (quantity != null) content.quantity = Math.round(quantity);
  if (amount != null) {
    content.amount = Math.round(amount);
    content.currency = currency;
  }

  return content.id || content.name || content.quantity != null || content.amount != null
    ? content
    : null;
};

const buildContentsData = (
  properties: EventProperties = {},
  fallbackContentType: string = 'product',
): Object => {
  const data = {
    type: 'contents',
  };
  const amount = toFiniteNumber(properties.value);

  if (amount != null) {
    data.amount = Math.round(amount);
    data.currency = properties.currency || 'KRW';
  }

  const rawItems =
    Array.isArray(properties.items) && properties.items.length ? properties.items : [properties];
  const contents = rawItems
    .map(item => buildContentItem(item, fallbackContentType))
    .filter(Boolean);
  if (contents.length) data.contents = contents;

  return data;
};

const buildPageViewData = (properties: EventProperties = {}): Object => ({
  type: 'contents',
  contents: [getCurrentPageContent(properties)],
});

const mapEvent = (eventName: string, properties: EventProperties = {}): ?Object => {
  switch (eventName) {
    case TRACKING_EVENTS.PAGE_VIEWED:
      return {
        name: 'page_viewed',
        data: buildPageViewData(properties),
      };
    case TRACKING_EVENTS.PRODUCT_VIEWED:
      return {
        name: 'contents_viewed',
        data: buildContentsData(properties, 'product'),
      };
    case TRACKING_EVENTS.PRODUCT_CART_ADDED:
      return {
        name: 'items_added',
        data: buildContentsData(properties),
      };
    case TRACKING_EVENTS.CHECKOUT_STARTED:
      return {
        name: 'checkout_started',
        data: buildContentsData(properties),
      };
    case TRACKING_EVENTS.REGISTRATION_COMPLETED:
      return {
        name: 'registration_completed',
        data: { type: 'customer_action' },
      };
    default:
      return null;
  }
};

export const track = (eventName: string, properties: EventProperties = {}): void => {
  const adsEvent = mapEvent(eventName, properties);
  if (!adsEvent || !isProduction() || !PIXEL_ID) return;
  try {
    if (!initialized) initialize();
    const oaiq = getQueue();
    if (!initialized || !oaiq) return;
    if (adsEvent.options) {
      oaiq('measure', adsEvent.name, adsEvent.data, adsEvent.options);
      return;
    }
    oaiq('measure', adsEvent.name, adsEvent.data);
  } catch (e) {
    // ignore
  }
};



// WEBPACK FOOTER //
// ./src/lib/tracking/openaiAdsTracker.js